import type { RouterConfig } from "@nuxt/schema";

// Derive RouteRecordRaw from RouterConfig to avoid vue-router module identity conflicts
// with @nuxt/schema's bundled vue-router types
type RouteRecordRaw = NonNullable<
  Parameters<NonNullable<RouterConfig["routes"]>>[0]
>[number];

export default {
  routes: (_routes) => {
    const routes: RouteRecordRaw[] = [
      {
        path: "/my-account",
        component: () => import("~/pages/my-account/my-account.vue"),
        meta: {
          layout: "web-responsive",
          middleware: ["auth"],
        },
        children: [
          {
            name: "my-account",
            path: "",
            component: () => import("~/pages/my-account/profile.vue"),
          },
          {
            name: "my-account.addresses",
            path: "addresses",
            component: () => import("~/pages/my-account/addresses.vue"),
            meta: {
              scrollToTop: false,
            },
          },
          {
            name: "my-account.order-history",
            path: "order-history",
            component: () => import("~/pages/my-account/order-history.vue"),
            meta: {
              scrollToTop: false,
            },
          },
          {
            name: "my-account.profile",
            path: "profile",
            component: () => import("~/pages/my-account/profile.vue"),
            meta: {
              scrollToTop: false,
            },
          },
        ],
      },
      {
        name: "auth",
        path: "/auth",
        component: () => import("~/pages/auth.vue"),
        meta: {
          layout: "web-responsive",
          middleware: ["guest"],
        },
      },
      {
        name: "home",
        path: "/home",
        component: () => import("~/pages/home.vue"),
        meta: {
          layout: "web-responsive",
        },
      },
      {
        name: "locations",
        path: "/locations",
        component: () => import("~/pages/locations.vue"),
        meta: {
          layout: "web-responsive",
        },
      },
      {
        name: "loyalty-program",
        path: "/loyalty-program",
        component: () => import("~/pages/loyalty-program.vue"),
        meta: {
          layout: "web-responsive",
        },
      },
      {
        name: "order-tracking",
        path: "/orders/:trackingId",
        component: () => import("~/pages/order-tracking.vue"),
        meta: {
          layout: "web-responsive",
          middleware: ["ensure-order-is-loaded-by-tracking-id"],
        },
      },
      {
        name: "order",
        path: "/:orderingMode?",
        component: () => import("~/pages/ordering/select-mode.vue"),
        meta: {
          layout: "web-responsive",
          key: "route-home-delivery-pickup",
          middleware: ["validate-ordering-mode"],
        },
      },
      {
        path: "/:orderingMode/:outletSlug",
        component: () =>
          import("~/pages/ordering/outlet").then((m) => ({
            default: m.PagesOutlet,
          })),
        meta: {
          layout: "web-responsive",
          key: "route-outlet",
          middleware: [
            "validate-ordering-mode",
            "ensure-store-is-loaded",
            "ensure-outlet-is-loaded",
            "ensure-loyalty-is-loaded",
            "ensure-outlet-metadata-is-loaded",
            "ensure-basket-is-loaded",
            "auto-add-item-to-basket",
            "auto-apply-loyalty-program",
          ],
        },
        children: [
          {
            name: "outlet",
            path: "",
            component: () =>
              import("~/pages/ordering/menus").then((m) => ({
                default: m.PagesMenus,
              })),
            meta: {
              scrollToTop: false,
            },
          },
          {
            name: "menu",
            path: "menus/:menuSlug",
            component: () => import("~/pages/ordering/menu.vue"),
          },
        ],
      },
      {
        name: "checkout",
        path: "/:orderingMode/:outletSlug/checkout",
        component: () =>
          import("~/pages/ordering/checkout").then((m) => ({
            default: m.PagesCheckout,
          })),
        meta: {
          layout: "web-responsive",
          middleware: [
            "validate-ordering-mode",
            "ensure-store-is-loaded",
            "ensure-outlet-is-loaded",
            "ensure-loyalty-is-loaded",
            "ensure-outlet-metadata-is-loaded",
            "ensure-basket-is-loaded",
          ],
        },
      },
      {
        name: "item",
        path: "/:orderingMode/:outletSlug/menus/:menuSlug/items/:itemId",
        redirect: (to) => {
          return {
            name: "menu",
            params: {
              orderingMode: to.params.orderingMode,
              outletSlug: to.params.outletSlug,
              menuSlug: to.params.menuSlug,
            },
            query: {
              "item-id": to.params.itemId,
            },
          };
        },
      },
    ];

    return routes;
  },

  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    }

    if (
      to.path === from.path ||
      to.params.menuSlug ||
      to.meta.scrollToTop === false
    ) {
      return false;
    }

    return {
      top: 0,
      left: 0,
      behavior: "smooth",
    };
  },
} satisfies RouterConfig;
